import { Listbox } from "@headlessui/react";
import { List } from "../../../components/List";
import { forwardRef, Fragment, useCallback, useEffect, useState } from 'react';
import { FontLoader, IFontItem } from "../../../../../lib/Fonts/FontLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisH } from "@fortawesome/free-solid-svg-icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection } from "lexical";
import { $getSelectionStyleValueForProperty, $patchStyleText } from '@lexical/selection';
import { FontManager } from "../../../../../lib/Fonts/FontManager";
import './FontFamily.css';
import clsx from "clsx";

const FontFamilyListItem = forwardRef<HTMLLIElement, { item: IFontItem }>(({ item }, ref) =>
{
    const { family } = item;
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() =>
    {
        let mount = true;
        FontLoader.instance.loadItem(item).then(() =>
        {
            if (mount) { setIsLoaded(true); }
        });
        return () => { mount = false; };
    },
    [item]);
    const element =
    <Listbox.Option value={family} as={Fragment}>
        {({ selected }) =>
            <li ref={ref} style={{ fontFamily: family }} className={clsx(isLoaded && 'loaded', selected && 'selected')}>
                <span className='name'>{family}&nbsp;</span>
                {isLoaded && <span className='preview'> : lorem ipsum dolor...</span>}
            </li>}
    </Listbox.Option>;
    return element;
});
FontFamilyListItem.displayName = 'FontListItem';

const DefaultFamily = '';

export function FontFamily()
{
    const [value, setValue] = useState(DefaultFamily);
    const [editor] = useLexicalComposerContext();
    const [filter, setFilter] = useState({ family: '', category: '', count: 8 });
    const items = [...FontManager.instance.filter(filter)];
    useEffect(() =>
    {
        return editor.registerUpdateListener(({ editorState }) =>
        {
            editorState.read(() =>
            {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) { return; }
                const family = $getSelectionStyleValueForProperty(selection, 'font-family', DefaultFamily);
                setValue(family);
            });
        });
    },
    [editor]);
    const onSelect = useCallback((value: string) =>
    {
        editor.update(() =>
        {
            const selection = $getSelection();
            if ($isRangeSelection(selection))
            {
                $patchStyleText(selection, { 'font-family': value });
            }
        });
    },
    [editor]);
    const onFamilyChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
        setFilter(({ category }) => ({ family: e.target.value, category, count: 8 }));
    };
    const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    {
        setFilter(({ family }) => ({ family, category: e.target.value, count: 8 }));
    };
    const onLoadMore = () =>
    {
        setFilter(({ family, category, count }) => ({ family, category, count: count + 6 }));
    };
    const element =
    <List value={value} title={value} onChange={onSelect} placeholder="Czcionka" className='menu font-list'>
        <li key="_filter">
            <input placeholder="Szukaj..." className="input form-control" value={filter.family} onChange={onFamilyChange} />
            <select className="form-control" onChange={onCategoryChange}>
                <option value={''}>All</option>
                <option value={'sans-serif'}>Sans serif</option>
                <option value={'serif'}>Serif</option>
                <option value={'monospace'}>Monospace</option>
                <option value={'handwriting'}>Handwriting</option>
                <option value={'display'}>Display</option>
                <option value={'icon'}>Icon</option>
            </select>
        </li>
        {items.map(item => (<FontFamilyListItem key={item.family} item={item} />))}
        <li onClick={onLoadMore}>
            <FontAwesomeIcon icon={faEllipsisH} />
        </li>
    </List>;
    return element;
}
